import HTTP from "@/http-common";
import { NbnOrder } from "@/models/NbnOrder";
import { getNbnProductType } from "@/utils/mappings";
import { submittedToast, zammadNoteNotAdded } from "@/utils/zammad-note";
import {
  firstPayloadValue,
  hasPersistedSelectValue,
  inferNtdModifications,
  isFibreProductType,
  isMultiPortNtd,
  isRealNtdId,
  normalizeFixedWirelessNtdType,
  resolveFibreNtdSelection,
  resolveStoredNtdId,
  resolveStoredNtdModifications,
  resolveStoredNtdType,
  resolveStoredProductTemplate,
  isV3SinglePortNtd,
  ntdAllPortsUsed,
  ntdHasFreePort,
  resolveConnectNtdIdSelection
} from "@/constants/nbn-order-item-constants";

export const state = {
  excludedProducts: [],
  isProductExcluded: false,
  nbnOrder: new NbnOrder(),
  orderAttachments: [],
  orderBandwidth: "",
  auditLogs: [],
  nbnOrderRecord: null,
  isNoChange: false
};

export const getters = {
  excludedProducts: state => {
    return state.excludedProducts;
  },
  isProductExcluded: state => {
    return state.isProductExcluded;
  },
  nbnOrder: state => {
    return state.nbnOrder;
  },
  orderAttachments: state => {
    return state.orderAttachments;
  },
  orderBandwidth: state => {
    return state.orderBandwidth;
  },
  auditLogs: state => {
    return state.auditLogs;
  },
  nbnOrderRecord: state => {
    return state.nbnOrderRecord;
  },
  isNoChange: state => {
    return state.isNoChange;
  }
};

export const mutations = {
  SET_EXCLUDED_PRODUCTS(state, val) {
    state.excludedProducts = val;
  },
  CHECK_PRODUCT(state, val) {
    if (
      state.excludedProducts.length > 0 &&
      state.excludedProducts.find(x => x.name === val)
    )
      state.isProductExcluded = true;
    else state.isProductExcluded = false;
  },
  RESET_PRODUCT(state) {
    state.isProductExcluded = false;
    // state.nbnOrder = new NbnOrder();
  },
  SET_NBN_ORDER(state, payload) {
    state.nbnOrder.customerAuthRef = payload.customerAuthReference;
    state.nbnOrder.customerRef = payload.customerReference;
    state.nbnOrder.customerFirstname = payload.customerFirstname;
    state.nbnOrder.customerSurname = payload.customerSurname;
    state.nbnOrder.customerContactEmail = payload.customerContactEmail;
    state.nbnOrder.customerContactNumber = payload.customerContactNo;
    state.nbnOrder.customerAuthorityDate = payload.customerAuthDate;
    state.nbnOrder.appointmentId = payload.appointmentId;
    state.nbnOrder.contractTerm = payload.contractLength;
    state.nbnOrder.radiusUsername = payload.radiusUsername;
    state.nbnOrder.radiusPassword = payload.radiusPassword;
    state.nbnOrder.avcId = payload.avcId;
    state.nbnOrder.isFibreConnect = payload.isFibreConnect;
    state.nbnOrder.serviceId = payload.serviceId;
    state.nbnOrder.status = payload.status;
    state.nbnOrder.wholesalerAccountNumber = payload.wholesalerAccountNumber;
    state.nbnOrder.cTag = payload.cTag ?? payload.CTag ?? "";
    state.nbnOrder.sTag = payload.sTag ?? payload.STag ?? "";
    const persistedNtdType = firstPayloadValue(payload.ntdType, payload.NtdType);
    const persistedProductTemplate = firstPayloadValue(
      payload.productTemplate,
      payload.ProductTemplate
    );
    const persistedNtdId = firstPayloadValue(
      payload.ntdID,
      payload.ntdId,
      payload.NtdId,
      payload.NtdID
    );
    const persistedNtdModifications = firstPayloadValue(
      payload.ntdModifications,
      payload.NtdModifications
    );
    state.nbnOrder.ntdModifications = resolveStoredNtdModifications(
      persistedNtdModifications,
      null
    );

    var isJsonString =
      typeof payload.request === "string" &&
      payload.request.trim().startsWith("{") &&
      payload.request.trim().endsWith("}");

    var request = "";

    if (isJsonString) {
      request = JSON.parse(payload.request);
      state.nbnOrder.isConnectOutstanding = request.isConnectOutstanding;
    }

    if (payload.orderType.toLowerCase() === "nbn") {
      state.nbnOrder.locationId = payload.locationId;
      state.nbnOrder.orderType =
        payload.requestType.toLowerCase() === "new service" ? "Connect" : "NBN";
      state.nbnOrder.productType = payload.productType;

      let NCASBandwidthProfile = [
        {
          text: "12/1 Mbps",
          value: "D12_U1_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/5 Mbps",
          value: "D25_U5_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/10 Mbps",
          value: "D25_U5-10_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "50/20 Mbps",
          value: "D25-50_U5-20_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "100/20 Mbps",
          value: "Home Fast",
          disabled: false
        },
        {
          text: "100/40 Mbps",
          value: "D25-100_U5-40_Mbps_TC4_P",
          disabled: false
        }
      ];

      let FTTCBandwidthProfile = [
        {
          text: "12/1 Mbps",
          value: "D12_U1_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/5 Mbps",
          value: "D25_U5_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/10 Mbps",
          value: "D25_U5-10_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "50/20 Mbps",
          value: "D25-50_U5-20_Mbps_TC4_P",
          disabled: false
        },
        { text: "100/20 Mbps", value: "Home Fast", disabled: false },
        {
          text: "100/40 Mbps",
          value: "D25-100_U5-40_Mbps_TC4_P",
          disabled: false
        }
      ];
      if (payload.sog6028) {
        FTTCBandwidthProfile = [
          {
            text: "12/1 Mbps",
            value: "D12_U1_Mbps_TC4_P",
            disabled: false
          },
          {
            text: "25/5 Mbps",
            value: "D25_U5_Mbps_TC4_P",
            disabled: false
          },
          {
            text: "25/10 Mbps",
            value: "D25_U10_Mbps_TC4_P",
            disabled: false
          },
          {
            text: "50/20 Mbps",
            value: "D50_U20_Mbps_TC4_P",
            disabled: false
          },
          { text: "100/20 Mbps", value: "Home Fast", disabled: false },
          {
            text: "100/40 Mbps",
            value: "D50-100_U20-40_Mbps_TC4_P",
            disabled: false
          }
        ];
      }

      let NWASBandwidthProfile = [
        {
          text: "12/1 Mbps",
          value: "D12_U1_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/5 Mbps",
          value: "D25_U5_Mbps_TC4_P",
          disabled: false
        },
        { text: "FW Plus", value: "FWPlus_TC4_P", disabled: false },
        { text: "250/20 Mbps", value: "FW Home Fast", disabled: false },
        { text: "400/40 Mbps", value: "FW Superfast", disabled: false }
      ];

      let NHASBandwidthProfile = [
        {
          text: "12/1 Mbps",
          value: "D12_U1_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/5 Mbps",
          value: "D25_U5_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/10 Mbps",
          value: "D25_U10_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "50/20 Mbps",
          value: "D50_U20_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "100/40 Mbps",
          value: "D100_U40_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "250/100 Mbps",
          value: "D250_U100_Mbps_TC4_P",
          disabled: false
        },
        { text: "500/50 Mbps", value: "Home Fast", disabled: false },
        {
          text: "750/50 Mbps",
          value: "Home Superfast",
          disabled: false
        },
        {
          text: "1000/100 Mbps",
          value: "Home Ultrafast",
          disabled: false
        }
      ];

      let NFASBandwidthProfile = [
        {
          text: "12/1 Mbps",
          value: "D12_U1_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/5 Mbps",
          value: "D25_U5_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "25/10 Mbps",
          value: "D25_U10_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "50/20 Mbps",
          value: "D50_U20_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "100/40 Mbps",
          value: "D100_U40_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "250/100 Mbps",
          value: "D250_U100_Mbps_TC4_P",
          disabled: false
        },
        { text: "500/50 Mbps", value: "Home Fast", disabled: false },
        {
          text: "500/200 Mbps",
          value: "D500_U200_Mbps_TC4_P",
          disabled: false
        },
        {
          text: "750/50 Mbps",
          value: "Home Superfast",
          disabled: false
        },
        {
          text: "1000/100 Mbps",
          value: "Home Ultrafast",
          disabled: false
        },
        {
          text: "1000/400 Mbps",
          value: "D1000_U400_Mbps_TC4_P",
          disabled: false
        }
      ];

      if (payload.productType === "FTTB" || payload.productType === "FTTN") {
        NCASBandwidthProfile.forEach(x => {
          if (x.text.includes(payload.avcdBandwidthProfile))
            state.nbnOrder.bandwidthProfile = x.value;
        });
      } else if (payload.productType === "Fixed Wireless") {
        NWASBandwidthProfile.forEach(x => {
          if (x.text.includes(payload.avcdBandwidthProfile))
            state.nbnOrder.bandwidthProfile = x.value;
        });
      } else if (payload.productType === "HFC") {
        NHASBandwidthProfile.forEach(x => {
          if (x.text.includes(payload.avcdBandwidthProfile))
            state.nbnOrder.bandwidthProfile = x.value;
        });
      } else if (payload.productType === "FTTC") {
        FTTCBandwidthProfile.forEach(x => {
          if (x.text.includes(payload.avcdBandwidthProfile))
            state.nbnOrder.bandwidthProfile = x.value;
        });
      } else if (isFibreProductType(payload.productType)) {
        var extraPlan = [
          {
            text: "250/100 Mbps",
            value: "D250_U100_Mbps_TC4_P",
            disabled: false
          }
        ];
        let fibreBandwidthOptions = [...NFASBandwidthProfile, ...extraPlan];

        fibreBandwidthOptions.forEach(x => {
          if (x.text.includes(payload.avcdBandwidthProfile))
            state.nbnOrder.bandwidthProfile = x.value;
        });
      }
    } else {
      state.nbnOrder.customerAuthRef = request.externalId;
      state.nbnOrder.orderType = request.orderType;
      var productType = [
        "Fibre",
        "FTTB",
        "FTTN",
        "FTTC",
        "Fixed Wireless",
        "HFC"
      ];

      if (
        request.productType !== undefined &&
        productType.includes(request.orderType)
      )
        state.nbnOrder.productType = request.productType;
      else state.nbnOrder.productType = payload.product;

      state.nbnOrder.onHold = request.onHold;
      state.nbnOrder.nbnAssureRequired = request.nbnAssureRequired
        ? "Yes"
        : "No";
      state.nbnOrder.id = payload.id;
      state.nbnOrder.existingRadiusUsername = request.existingRadiusUsername;
      state.nbnOrder.existingRadiusPassword = request.existingRadiusPassword;
      if (request.isIPoE !== undefined) {
        state.nbnOrder.isIpoe = request.isIPoE;
      } else {
        state.nbnOrder.isIpoe = false;
      }

      if (payload.priId != null && payload.priId != "") {
        state.nbnOrder.productId = payload.priId;
      }

      if (payload.nbnOrderId != null && payload.nbnOrderId != "") {
        state.nbnOrder.nbnOrderId = payload.nbnOrderId;
      }

      state.nbnOrder.status = payload.status;

      if (request.nbnAssureRequired) {
        state.nbnOrder.nbnAssureOption = request.nbnAssure;
      }

      state.nbnOrder.simRequired = request.simRequired ? "Yes" : "No";
      if (request.simRequired) {
        state.nbnOrder.simAddress = request.simDeliveryAddress;
      } else {
        state.nbnOrder.usim = request.usim;
      }

      state.nbnOrder.hardwareRequired = request.HardwareRequired ? "Yes" : "No";

      if (request.HardwareRequired) {
        state.nbnOrder.hardwareOption = request.HardwareModem;
        state.nbnOrder.hardwareAddress = request.HardwareDeliveryAddress;

        if (
          request.HardwareDeliveryContactName &&
          request.HardwareDeliveryContactNumber
        ) {
          state.nbnOrder.hardwareContactName =
            request.HardwareDeliveryContactName;
          state.nbnOrder.hardwareContactNumber =
            request.HardwareDeliveryContactNumber;
        }
      }

      if (payload.scheduledDateTime != null) {
        state.nbnOrder.connectionDateRequire = "Yes";
        state.nbnOrder.scheduledDateTime = payload.scheduledDateTime;
        state.nbnOrder.scheduledDateTimeString = payload.scheduledDateTime.replace(
          "T",
          " "
        );
      } else if (request.onHold) {
        state.nbnOrder.connectionDateRequire = "Held";
      }
      state.nbnOrder.staticIP = payload.staticIP;

      if (request.relatedParty !== undefined) {
        state.nbnOrder.partyId = request.relatedParty[0].id;
      }

      if (request.ipRange != null) {
        state.nbnOrder.ipRange = request.ipRange;
      }

      if (request.realizedResource.length > 0) {
        if (
          request.realizedResource[0].product.productSpecification.id != null &&
          request.realizedResource[0].product.productSpecification.id.includes("NTD")
        ) {
          state.isNoChange = request.realizedResource[0].action === "NO CHANGE";
          const requestNtdId = request.realizedResource[0].product.id;
          if (!hasPersistedSelectValue(persistedNtdId)) {
            const payloadNtdId =
              payload.ntdID || payload.ntdId || payload.NtdId || payload.NtdID;
            state.nbnOrder.ntdId = isRealNtdId(payloadNtdId)
              ? payloadNtdId
              : requestNtdId || payloadNtdId || "";
          }
          if (isRealNtdId(persistedNtdId)) {
            state.nbnOrder.existingNtdId = persistedNtdId;
          } else if (isRealNtdId(state.nbnOrder.ntdId)) {
            state.nbnOrder.existingNtdId = state.nbnOrder.ntdId;
          } else if (isRealNtdId(requestNtdId)) {
            state.nbnOrder.existingNtdId = requestNtdId;
          }
          const isJsonSQString =
          typeof payload._sqResponse === "string" &&
          payload._sqResponse.trim().startsWith("{") &&
          payload._sqResponse.trim().endsWith("}");

          if (!hasPersistedSelectValue(persistedNtdType) && isJsonSQString) {
            const requestSQ = JSON.parse(payload._sqResponse);
            state.nbnOrder.ntdType = requestSQ.sq?.siteRestriction?.supportingResource?.find(x=> x.id == state.nbnOrder.ntdId)?.NTDType ?? "";

          }
        }

        if (!hasPersistedSelectValue(persistedNtdType)) {
          const ntdFromProduct = request.realizedResource[0].product.ntdType;
          const ntdFromProps =
            request.realizedResource[0].product.additionalProperties &&
            request.realizedResource[0].product.additionalProperties.ntdType;
          if (ntdFromProduct != null) {
            state.nbnOrder.ntdType = ntdFromProduct;
          } else if (ntdFromProps != null) {
            state.nbnOrder.ntdType = ntdFromProps;
          }
        }

        if (request.realizedResource[0].product.macAddress != null) {
          state.nbnOrder.macAddress =
            request.realizedResource[0].product.macAddress;
        }

        if (request.realizedResource[0].product.serialNumber != null) {
          state.nbnOrder.serialNumber =
            request.realizedResource[0].product.serialNumber;
        }

        if (
          request.realizedResource[0].product.id != null &&
          request.realizedResource[0].product.id.includes("CPI")
        ) {
          state.nbnOrder.cpiId = request.realizedResource[0].product.id;
        }

        if (
          request.realizedResource.length > 1 &&
          request.realizedResource[1].product.portId !== undefined
        ) {
          state.nbnOrder.portId = request.realizedResource[1].product.portId;
        }

        if (
          request.realizedResource[0].product.dslStabilityProfile !== undefined
        ) {
          state.nbnOrder.dslStabilityProfile =
            request.realizedResource[0].product.dslStabilityProfile;
        }

        if (
          request.realizedResource.length > 1 &&
          request.realizedResource[1].product.id === "UNI-E"
        ) {
          state.nbnOrder.interfaceBandwidth =
            request.realizedResource[1].product.interfaceBandwidth.amount +
            " " +
            request.realizedResource[1].product.interfaceBandwidth.unit;
          state.nbnOrder.interfaceType =
            request.realizedResource[1].product.interfaceType;
          state.nbnOrder.ovcType = request.realizedResource[1].product.ovcType;
          state.nbnOrder.tpid = request.realizedResource[1].product.tpid;
        }
      }

      if (request.realizedService[0].product.cvcId !== undefined) {
        state.nbnOrder.cvcId = request.realizedService[0].product.cvcId;
      }

      if (request.realizedService[0].product.bandwidthProfile !== undefined) {
        state.nbnOrder.bandwidthProfile = payload.avcdBandwidthProfile;
      }

      if (request.realizedService[0].product.uniVlanId !== undefined) {
        state.nbnOrder.tagVlan = request.realizedService[0].product.uniVlanId;
        state.nbnOrder.productTemplate =
          request.productOffering.id +
          "/" +
          request.productOffering.version +
          "/tag";
      } else {
        state.nbnOrder.productTemplate =
          request.productOffering.id +
          "/" +
          request.productOffering.version +
          "/default";
      }

      if (request.orderItem && request.orderItem.product.endUserType) {
        state.nbnOrder.endUserType = request.orderItem.product.endUserType;
      }

      if (
        request.orderItem &&
        request.orderItem.product.installationWorkforce
      ) {
        state.nbnOrder.installationWorkforce =
          request.orderItem.product.installationWorkforce;

        if (state.nbnOrder.installationWorkforce == "NBN Co Install End User") {
          state.nbnOrder.shippingType = request.shippingType;
          state.nbnOrder.authToLeave = request.authToLeave;
          if (
            request.shippingContact != null &&
            request.shippingContact != "null"
          ) {
            var shippingContact = JSON.parse(request.shippingContact);
            state.nbnOrder.contactFirstname = shippingContact.DeliveryFirstName;
            state.nbnOrder.contactSurname = shippingContact.DeliveryLastName;
            state.nbnOrder.contactNumber = shippingContact.DeliveryNumber;
            state.nbnOrder.contactEmail = shippingContact.DeliveryEmail;
          }
          if (
            request.shippingType === "Alternate Address" &&
            request.shippingAddress != null
          ) {
            var shippingAddress = JSON.parse(request.shippingAddress);
            state.nbnOrder.shippingAddress = {
              fullAddress: shippingAddress.FullAddress,
              addressLine: shippingAddress.AddressLine,
              suburb: shippingAddress.Suburb,
              state: shippingAddress.State,
              postcode: shippingAddress.Postcode
            };
          }
        }
      }
      state.nbnOrder.locationId = request.relatedPlace.id;
      state.nbnOrder.serviceClass = payload.serviceClass;

      if (
        payload.requestType == "Connect" &&
        request.realizedResource.length > 0 &&
        !request.realizedResource[0].product.id
      ) {
        if (
          request.productType == "FTTC" &&
          (payload.serviceClass == "33" || payload.serviceClass == "34")
        )
          state.nbnOrder.cpiId = "Additional Line Installation";
        else if (
          request.productType == "HFC" &&
          (payload.serviceClass == "23" || payload.serviceClass == "24") &&
          !hasPersistedSelectValue(persistedNtdId) &&
          !isRealNtdId(state.nbnOrder.ntdId)
        )
          state.nbnOrder.ntdId = "Additional Line Installation";
        else if (
          isFibreProductType(request.productType) &&
          payload.serviceClass === "3" &&
          !hasPersistedSelectValue(persistedNtdId) &&
          !isRealNtdId(state.nbnOrder.ntdId)
        )
          state.nbnOrder.ntdId = "Additional NTD Required";
      }

      if (request.reasonCode != null) {
        state.nbnOrder.replaceNtd = true;
        const reasonNtdId = request.reasonCode.reason.split(" ")[0];
        if (
          isRealNtdId(reasonNtdId) &&
          !isRealNtdId(state.nbnOrder.ntdId) &&
          !hasPersistedSelectValue(persistedNtdId)
        ) {
          state.nbnOrder.ntdId = reasonNtdId;
        } else if (isRealNtdId(reasonNtdId)) {
          state.nbnOrder.existingNtdId = reasonNtdId;
        }
      }

      if (
        !isRealNtdId(state.nbnOrder.ntdId) &&
        !hasPersistedSelectValue(persistedNtdId)
      ) {
        const fallbackNtdId =
          payload.ntdID || payload.ntdId || payload.NtdId || payload.NtdID;
        if (isRealNtdId(fallbackNtdId)) {
          state.nbnOrder.ntdId = fallbackNtdId;
          state.nbnOrder.existingNtdId = fallbackNtdId;
        }
      }
    }

    if (!state.nbnOrder.ntdModifications) {
      const requestPayload =
        request && typeof request === "object"
          ? request
          : typeof payload.request === "object"
          ? payload.request
          : null;
      if (requestPayload) {
        state.nbnOrder.ntdModifications = inferNtdModifications(requestPayload);
      }
    }

    state.nbnOrder.ntdType = resolveStoredNtdType(
      persistedNtdType,
      state.nbnOrder.ntdType
    );
    state.nbnOrder.productTemplate = resolveStoredProductTemplate(
      persistedProductTemplate,
      state.nbnOrder.productTemplate
    );
    state.nbnOrder.ntdId = resolveStoredNtdId(
      persistedNtdId,
      state.nbnOrder.ntdId
    );
    state.nbnOrder.ntdModifications = resolveStoredNtdModifications(
      persistedNtdModifications,
      state.nbnOrder.ntdModifications
    );
  },
  RESET_NBN_ORDER(state) {
    state.nbnOrder = new NbnOrder();
    state.nbnOrderRecord = null;
  },
  SET_NBN_ORDER_RECORD(state, payload) {
    state.nbnOrderRecord = payload ? Object.assign({}, payload) : null;
  },
  SET_PRODUCT_TYPE(state, payload) {
    if (payload !== "" && payload !== null) {
      state.nbnOrder.productType = getNbnProductType(payload);
    }
  },
  RESET_PRODUCT_TYPE(state) {
    state.nbnOrder.productType = "";
  },
  SET_NBN_ORDER_TYPE(state, payload) {
    state.nbnOrder.orderType = payload;
  },
  RESET_NBN_ORDER_TYPE(state) {
    state.nbnOrder.orderType = "";
  },
  SET_NTD_ID(state, ntdId) {
     state.nbnOrder.ntdId = ntdId;
  },

  SET_EXISTING_NTD_ID(state, existingNtdId) {
    state.nbnOrder.existingNtdId = existingNtdId || "";
  },

  SET_NTD_ID_ORDER_ITEM(state, payload) {
    state.nbnOrder.ntdId = payload;
    if (isRealNtdId(payload)) {
      state.nbnOrder.existingNtdId = payload;
    }
  },

  RESET_NTD_ID(state) {
    state.nbnOrder.ntdId = "";
    state.nbnOrder.existingNtdId = "";
  },

  SET_NTD_TYPE(state, payload) {
    if (
      payload.sq.siteRestriction.supportingTechnology.serviceabilityClass ===
        "3" ||
      payload.sq.siteRestriction.supportingTechnology.serviceabilityClass ===
        "6"
    ) {
      if (payload.sq.siteRestriction.supportingResource.length === 1) {
        const ntdType =
          payload.sq.siteRestriction.supportingResource[0].NTDType;
        const isWireless =
          payload.sq.siteRestriction.supportingTechnology
            .primaryAccessTechnology === "Wireless";
        state.nbnOrder.ntdType = isWireless
          ? normalizeFixedWirelessNtdType(ntdType)
          : ntdType;
      }
    }

    else state.nbnOrder.ntdType = "";
  },

  SET_NTD_TYPE_FROM_FORM(state, payload) {
    state.nbnOrder.ntdType = payload;
  },

  RESET_NTD_TYPE(state) {
    state.nbnOrder.ntdType = "";
  },

  RESET_NTD_MODIFICATION(state) {
    state.nbnOrder.ntdModifications = "";
  },
  SET_CPI_ID(state, payload) {
    if (
      payload.sq.siteRestriction.supportingTechnology
        .primaryAccessTechnology === "Fibre To The Node"
    ) {
      let areAllLinesInUse = payload.sq.siteRestriction.supportingResource.every(
        service => service.NBNServiceStatus === "Line In Use"
      );
      if (areAllLinesInUse && state.nbnOrder.orderType !== "Transfer") {
        state.nbnOrder.cpiId = "Additional Line Installation";
      } else {
        if (
          payload.sq.siteRestriction.supportingResource &&
          payload.sq.siteRestriction.supportingResource.length > 0
        ) {
          if (payload.sq.siteRestriction.supportingResource.length === 1) {
            state.nbnOrder.cpiId =
              payload.sq.siteRestriction.supportingResource[0].id;
          }
        } else {
          state.nbnOrder.cpiId = "New Line Installation";
        }
      }
    }
    // else if (
    //   payload.sq.siteRestriction.supportingTechnology.serviceabilityClass ===
    //     "12" ||
    //   payload.sq.siteRestriction.supportingTechnology.serviceabilityClass ===
    //     "13"
    // ) {
    //   if (
    //     payload.sq.siteRestriction.supportingResource &&
    //     payload.sq.siteRestriction.supportingResource.length > 0
    //   ) {
    //     if (payload.sq.siteRestriction.supportingResource.length === 1) {
    //       state.nbnOrder.cpiId =
    //         payload.sq.siteRestriction.supportingResource[0].id;
    //     } else state.nbnOrder.cpiId = "";
    //   } else {
    //     state.nbnOrder.cpiId = "New Line Installation";
    //   }
    // }
  },
  RESET_CPI_ID(state) {
    state.nbnOrder.cpiId = "";
  },
  SET_INSTALLATION_WORKFORCE(state, payload) {
    state.nbnOrder.installationWorkforce = payload;
  },
  RESET_INSTALLATION_WORKFORCE(state) {
    state.nbnOrder.installationWorkforce = "";
  },
  SET_FILES(state, payload) {
    state.orderAttachments = payload;
  },
  RESET_EXISTING_RADIUS_DETAILS(state) {
    state.nbnOrder.existingRadiusUsername = "";
    state.nbnOrder.existingRadiusPassword = "";
  },
  RESET_NEW_RADIUS_DETAILS(state) {
    state.nbnOrder.radiusUsername = "";
    state.nbnOrder.radiusPassword = "";
  },
  SET_IS_FIBRE_CONNECT(state, payload) {
    state.nbnOrder.isFibreConnect = payload;
  },
  SET_ORDER_BANDWIDTH(state, payload) {
    state.orderBandwidth = payload;
  },
  SET_AUDIT_LOGS(state, payload) {
    state.auditLogs = payload;
  },
  SET_NTD_MODIFICATION_WITH_CONTEXT(state, payload) {
    state.nbnOrder.ntdModifications = payload.ntdModifications;
    state.nbnOrder.contractTerm = payload.contractTerm;
    state.nbnOrder.bandwidthProfile = payload.bandwidthProfile;
  },
  SET_NTD_MODIFICATIONS(state, payload) {
    state.nbnOrder.ntdModifications = payload;
  },
};

export const actions = {
  async saveProduct({ dispatch }, productName) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post(
        `OptusNBN/SaveExcludedProduct/${productName}`
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async getExcludedProducts({ dispatch, commit }, primaryAccessTechnology) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.get(`OptusNBN/GetExcludedProducts`);
      if (result.data !== "") {
        commit("SET_EXCLUDED_PRODUCTS", result.data);
        commit("CHECK_PRODUCT", primaryAccessTechnology);
      }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async productQualification({ dispatch }, order) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/NbnProductQualification", order);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async createNbnOrderAsync({ dispatch }, order) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/CreateNbnOrder", order);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      if (zammadNoteNotAdded(result)) {
        dispatch(
          "httpResponse/setHttpResponse",
          submittedToast("Order", result),
          { root: true }
        );
      }
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        {
          text: e.response.data ? e.response.data : "Unable to create order",
          color: "error"
        },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async uploadFiles({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let formData = new FormData();
      payload.files.forEach(file => {
        formData.append("files", file);
      });
      let result = await HTTP.post(
        "OptusNBN/UploadFiles/" + payload.customerAuthRefs,
        formData
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async getFiles({ dispatch, commit }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.get(
        "OptusNBN/ListFileFromCustomerAuthRef/" + payload
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      commit("SET_FILES", result.data);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async getFile({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.get(
        `OptusNBN/PreviewFile/${payload.custAuth}/${payload.file}`
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async createOrganisationAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/CreateOrganisation", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async updateOrganisationAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/UpdateOrganisation", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async getOrganisationsAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/GetNbnOrganisations", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async searchOrganisationsAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/SearchNbnOrganisations", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async createQuoteAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/CreateNbnQuotes", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async updateQuoteAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/UpdateNbnQuotes", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async getQuotesAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/GetNbnQuotes", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async getQuoteAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/GetNbnQuote", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async activateHoldOrderAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/ReSendingNbnOrder", payload);
      // if (result.data !== "") {
      //   commit("SET_EXCLUDED_PRODUCTS", result.data);
      //   commit("CHECK_PRODUCT", order);
      // }
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.statusText, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async cancelNbnOrderAsync({ dispatch }, order) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/CancelNbnOrder", order);
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      dispatch(
        "httpResponse/setHttpResponse",
        submittedToast("Withdrawal", result),
        { root: true }
      );
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.data, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async disconnectNbnServiceAsync({ dispatch }, order) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/DisconnectNbnService", order);
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      dispatch(
        "httpResponse/setHttpResponse",
        submittedToast("Disconnection", result),
        { root: true }
      );
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.data, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async updateNbnOrderAsync({ dispatch }, order) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      order.wholesalerAccountNumber =
        parseInt(order.wholesalerAccountNumber) || 0;
      let result = await HTTP.post("OptusNBN/UpdateNbnOrder", order);
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: e.response.data, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async correctNbnOrderAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/CorrectNbnOrder", payload);
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      dispatch(
        "httpResponse/setHttpResponse",
        {
          text:
            (result.data && result.data.message) ||
            "NBN order correction applied.",
          color: "success"
        },
        { root: true }
      );
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        {
          text:
            (e.response && e.response.data) ||
            "Unable to apply NBN order correction.",
          color: "error"
        },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async diagnoseNbnOrderSpeedChangeAsync({ dispatch }, orderId) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.get("OptusNBN/DiagnoseNbnOrderSpeedChange", {
        params: { orderId },
      });
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result.data);
    } catch (e) {
      const message = e.response?.data || "Speed change diagnosis failed.";
      dispatch(
        "httpResponse/setHttpResponse",
        { text: message, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async repairNbnOrderSpeedChangeAsync(
    { dispatch },
    { orderId, dryRun = true }
  ) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/RepairNbnOrderSpeedChange", {
        orderId,
        dryRun,
      });
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      const applied = dryRun || result.data?.changed === true;
      const summary = dryRun
        ? "Repair preview completed."
        : applied
        ? "Speed change repair applied."
        : "No repair changes were saved.";
      dispatch(
        "httpResponse/setHttpResponse",
        { text: summary, color: applied ? "success" : "warning" },
        { root: true }
      );
      return Promise.resolve(result.data);
    } catch (e) {
      const message = e.response?.data || "Speed change repair failed.";
      dispatch(
        "httpResponse/setHttpResponse",
        { text: message, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },
  async getServiceRadiusAuthDetails({ dispatch }, payload) {
    try {
      let result = await HTTP.post("OptusNBN/GetAuthDetails", payload);
      return Promise.resolve(result.data);
    } catch (e) {
      let message = e.response.data;
      dispatch(
        "httpResponse/setHttpResponse",
        { text: message, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  resetProduct({ commit }) {
    commit("RESET_PRODUCT");
  },

  setNbnOrderDetails({ commit }, payload) {
    commit("SET_NBN_ORDER_RECORD", payload);
    commit("SET_NBN_ORDER", payload);
  },

  resetNbnOrderDetails({ commit }) {
    commit("RESET_NBN_ORDER");
  },

  setProductType({ commit }, payload) {
    commit("SET_PRODUCT_TYPE", payload);
  },

  resetProductType({ commit }) {
    commit("RESET_PRODUCT_TYPE");
  },

  setNbnOrderType({ commit }, payload) {
    commit("SET_NBN_ORDER_TYPE", payload);
  },
  resetNbnOrderType({ commit }) {
    commit("RESET_NBN_ORDER_TYPE");
  },

  setNtdId({ commit, state, rootState }, payload) {
    const existingNtdFromSq =
      (payload?.sq?.siteRestriction?.supportingResource || []).find(item =>
        isRealNtdId(item?.id)
      )?.id || "";

    if (rootState.order?.isOriginal && isRealNtdId(state.nbnOrder.ntdId)) {
      commit("SET_EXISTING_NTD_ID", existingNtdFromSq || state.nbnOrder.ntdId);
      return;
    }

    const sog8191 = rootState.featureFlag?.featureFlags?.sog8191;
    const orderType = state.nbnOrder.orderType;
    const isConnect = orderType === "Connect";
    const isTransfer = orderType === "Transfer";
    const isFibre =
      isFibreProductType(state.nbnOrder.productType) ||
      state.nbnOrder.isFibreConnect;

    const resources = payload?.sq?.siteRestriction?.supportingResource || [];
    const serviceClass =
      payload?.sq?.siteRestriction?.supportingTechnology?.serviceabilityClass;

    // Greenfield SC � installation required (no usable on-site NTD for Connect)
    if (
      isConnect &&
      (serviceClass === "1" || serviceClass === "2" || serviceClass === "5")
    ) {
      const existing =
        resources.find(r => isRealNtdId(r?.id))?.id || resources[0]?.id || "";
      if (existing) {
        commit("SET_EXISTING_NTD_ID", existing);
      }
      commit("SET_NTD_ID", "NTD Installation Required");
      return;
    }

    if (resources.length === 0) {
      return;
    }

    if (sog8191) {
      if (isTransfer) {
        let ntdId = null;
        let existingNtdId = existingNtdFromSq || "";
        for (const resource of resources) {
          if ((resource.uniPortD || []).some(port => port.status === "Used")) {
            ntdId = resource.id;
            existingNtdId = resource.id;
            break;
          }
        }
        if (!existingNtdId && resources[0]?.id) {
          existingNtdId = resources[0].id;
        }
        commit("SET_EXISTING_NTD_ID", existingNtdId);
        commit("SET_NTD_ID", ntdId);
        return;
      }

      if (isConnect || !orderType) {
        const selection = resolveConnectNtdIdSelection(resources, { isFibre });
        let ntdId = selection.ntdId;
        let existingNtdId = selection.existingNtdId;

        if (ntdId === "NTD Installation Required") {
          const v3Used = resources.find(
            r => isV3SinglePortNtd(r) && ntdAllPortsUsed(r)
          );
          existingNtdId = v3Used?.id || existingNtdId;
        } else if (ntdId === "Additional NTD Required") {
          const multiUsed = resources.find(
            r => isMultiPortNtd(r) && ntdAllPortsUsed(r)
          );
          existingNtdId = multiUsed?.id || existingNtdId;
        }

        if (!existingNtdId && resources[0]?.id) {
          existingNtdId = resources[0].id;
        }

        commit("SET_EXISTING_NTD_ID", existingNtdId);
        commit("SET_NTD_ID", ntdId);
        return;
      }
    }

    const isFibreSc =
      isFibreProductType(state.nbnOrder.productType) &&
      (serviceClass === "3" || serviceClass === "6");

    if (isFibreSc) {
      const selection = resolveFibreNtdSelection(resources, {
        isConnect,
        isTransfer,
        sog8191
      });
      commit("SET_EXISTING_NTD_ID", selection.existingNtdId);
      commit("SET_NTD_ID", selection.ntdId);
      return;
    }

    const resource = resources.length === 1 ? resources[0] : null;
    const existingNtdId =
      resources.find(item => isRealNtdId(item?.id))?.id || "";

    let ntdId = null;
    if (resource && ntdHasFreePort(resource)) {
      ntdId = resource.id;
    } else if (sog8191 && isConnect && isFibre) {
      ntdId = isV3SinglePortNtd(resource)
        ? "NTD Installation Required"
        : "Additional NTD Required";
    }

    commit("SET_EXISTING_NTD_ID", existingNtdId);
    commit("SET_NTD_ID", ntdId);
  },

  setNtdType({ commit, state, rootState }, payload) {
    if (rootState.order?.isOriginal && state.nbnOrder.ntdType) {
      return;
    }
    commit("SET_NTD_TYPE", payload);
  },

  setNtdTypeFromForm({ commit }, payload) {
    commit("SET_NTD_TYPE_FROM_FORM", payload);
  },

  setNtdIdOrderItem({ commit }, payload) {
    commit("SET_NTD_ID_ORDER_ITEM", payload);
  },

  setExistingNtdId({ commit }, payload) {
    commit("SET_EXISTING_NTD_ID", payload);
  },

  resetNtdId({ commit }) {
    commit("RESET_NTD_ID");
  },

  
  resetNtdType({ commit }) {
    commit("RESET_NTD_TYPE");
  },

  resetNtdModification({ commit }) {
    commit("RESET_NTD_MODIFICATION");
  },

  setCpiId({ commit }, payload) {
    commit("SET_CPI_ID", payload);
  },

  resetCpiId({ commit }) {
    commit("RESET_CPI_ID");
  },

  setInstallationWorkforce({ commit }, payload) {
    commit("SET_INSTALLATION_WORKFORCE", payload);
  },
  resetInstallationWorkforce({ commit }) {
    commit("RESET_INSTALLATION_WORKFORCE");
  },

  resetExistingRadiusDetails({ commit }) {
    commit("RESET_EXISTING_RADIUS_DETAILS");
  },

  resetNewRadiusDetails({ commit }) {
    commit("RESET_NEW_RADIUS_DETAILS");
  },

  setIsFibreConnect({ commit }, payload) {
    commit("SET_IS_FIBRE_CONNECT", payload);
  },

  setNtdModificationWithContext({ commit }, payload) {
    commit("SET_NTD_MODIFICATION_WITH_CONTEXT", payload);
  },

  setNtdModifications({ commit }, payload) {
    commit("SET_NTD_MODIFICATIONS", payload);
  },
  async retrieveOrderBandwidth({ commit, dispatch }, payload) {
    try {
      let result = await HTTP.get(
        `OptusNBN/GetOrderBandwidthProfile/${payload}`
      );
      commit("SET_ORDER_BANDWIDTH", result.data.data);
      return Promise.resolve(result.data);
    } catch (e) {
      let message = e.response.data;
      dispatch(
        "httpResponse/setHttpResponse",
        { text: message, color: "error" },
        { root: true }
      );
      return Promise.reject(e);
    }
  },
  async retrieveAuditLogs({ commit, dispatch }) {
    try {
      let result = await HTTP.get(`OptusNBN/GetAuditLogs`);
      commit("SET_AUDIT_LOGS", result.data.data);
      return Promise.resolve(result.data);
    } catch (e) {
      let message = e.response.data;
      dispatch(
        "httpResponse/setHttpResponse",
        { text: message, color: "error" },
        { root: true }
      );
      return Promise.reject(e);
    }
  },
  async retrieveTagDetails({ dispatch }, payload) {
    try {
      let result = await HTTP.get(`OptusNBN/GetAdvancedCTag/${payload}`);
      return Promise.resolve(result.data);
    } catch (e) {
      let message = e.response.data;
      dispatch(
        "httpResponse/setHttpResponse",
        { text: message, color: "error" },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  },

  async bulkImportExistingOrderAsync({ dispatch }, payload) {
    dispatch("processLoader/setProcessActive", {}, { root: true });
    try {
      let result = await HTTP.post("OptusNBN/BulkImportExistingOrder", payload);
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.resolve(result);
    } catch (e) {
      dispatch(
        "httpResponse/setHttpResponse",
        {
          text: e.response?.data ? e.response.data : "Unable to import order",
          color: "error"
        },
        { root: true }
      );
      dispatch("processLoader/setProcessInactive", {}, { root: true });
      return Promise.reject(e);
    }
  }
};
